import type { NuxtApp } from '#app'
import { isValidSanctumToken } from '~/utils/isValidSanctumToken'
import { randomUuid } from '~/utils/randomUuid'
import { SessionTokenError } from '~/utils/SessionTokenError'

const SANCTUM_TOKEN_KEY = 'sanctum.token'
const SANCTUM_TOKEN_OWNER_KEY = 'sanctum.token.owner'
const TAB_LOGGED_OUT_KEY = 'sanctum.token.tabLoggedOut'
const TAB_ID_KEY = 'sanctum.tabId'

/**
 * A random id naming this tab, persisted in this tab's own sessionStorage
 * for its whole lifetime. The only thing it's used for: recognizing whether
 * a remembered token sitting in the shared localStorage was written by THIS
 * tab (so it's safe to supersede on this tab's own say-so) versus some other
 * tab entirely (which must never be touched from here).
 */
const tabId = (): string => {
  const existing = sessionStorage.getItem(TAB_ID_KEY)
  if (existing !== null) return existing
  const generated = randomUuid()
  sessionStorage.setItem(TAB_ID_KEY, generated)
  return generated
}

/**
 * A missing owner tag (no other tab has ever run this ownership-aware code
 * against this entry -- e.g. a token written before this fix shipped, or by
 * a test that seeds localStorage directly) is presumed owned by this tab,
 * not foreign: the pre-existing, safer default was "this tab's own session"
 * whenever sessionStorage was empty, and only an *explicitly different*
 * recorded tab id should override that presumption.
 */
const ownsLocalStorageToken = (): boolean => {
  const owner = localStorage.getItem(SANCTUM_TOKEN_OWNER_KEY)
  return owner === null || owner === tabId()
}

const clearLocalStorageToken = (): void => {
  localStorage.removeItem(SANCTUM_TOKEN_KEY)
  localStorage.removeItem(SANCTUM_TOKEN_OWNER_KEY)
}

const readToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const sessionToken = sessionStorage.getItem(SANCTUM_TOKEN_KEY)
  if (sessionToken !== null) {
    if (isValidSanctumToken(sessionToken)) return sessionToken
    sessionStorage.removeItem(SANCTUM_TOKEN_KEY)
    return undefined
  }
  if (sessionStorage.getItem(TAB_LOGGED_OUT_KEY) !== null) return undefined
  const localToken = localStorage.getItem(SANCTUM_TOKEN_KEY)
  if (localToken === null) return undefined
  if (isValidSanctumToken(localToken)) return localToken
  clearLocalStorageToken()
  return undefined
}

/**
 * Registers `nuxt-auth-sanctum`'s token mode with Web Storage (INFRA-46): no
 * cookie anywhere in this app's auth system. `remember` picks which storage
 * `set()` writes a new token to -- localStorage persists past tab close and
 * is shared by every tab on this origin; sessionStorage is tab-scoped, so a
 * value sitting there can only ever be this tab's own -- never another
 * tab's, since other tabs each have their own separate sessionStorage.
 *
 * localStorage has no such built-in scoping, so every write there is
 * tagged with this tab's own random id (`ownsLocalStorageToken`). That tag
 * is the only way to tell "this tab's own earlier remembered login, now
 * safe to supersede" apart from "a different, still-open tab's active
 * remembered session, which must never be touched from here" -- both look
 * identical as plain stored strings. Without it, either (a) writing a new
 * non-remembered token always clears the old localStorage entry, which
 * silently logs out a different tab's real session the moment this tab
 * logs in with any remember choice, or (b) it never clears it, which lets
 * a stale remembered token from this same tab's own earlier login resurface
 * and reauthenticate as that old account once this tab is later closed and
 * reopened (sessionStorage gone, localStorage still holding the un-cleared
 * old value) even though the user explicitly chose not to be remembered on
 * their most recent, later login in that same tab. Only the tag lets both
 * of those real scenarios resolve correctly at once.
 *
 * `set()` with `token: undefined` (logout) clears only whichever storage
 * this tab's own `get()` would actually have read from -- sessionStorage if
 * present, else localStorage but only when this tab owns that entry --
 * mirroring `get()`'s own precedence, so logging out one tab can only ever
 * remove that tab's own active token, never a different tab's shared,
 * still-valid remembered session. It is also idempotent: once the
 * tab-logout marker below is set, a repeat call (e.g.
 * `useSanctumAuth().logout()` internally clearing the token, followed by
 * this app's own defensive clear in the same error-handling path) is a
 * no-op, so the second call can never fall through to the "sessionStorage
 * was already empty" branch and delete a different tab's owned entry.
 *
 * `get()` checks sessionStorage before localStorage for the same reason:
 * checking localStorage first would let a second tab's remembered login
 * silently override this tab's own non-remembered session token, running
 * this tab's requests as the other tab's account.
 *
 * Logout also sets a tab-local (sessionStorage) marker that suppresses the
 * localStorage fallback for the rest of this tab's lifetime: without it, a
 * tab that just logged out of its own session would immediately "become"
 * whatever remembered account another tab has sitting in the still-shared
 * localStorage on its very next `get()` (e.g. after a reload) -- an
 * explicit logout in one tab must never silently log that tab back in as a
 * different account. A real token `set()` clears the marker, since that is
 * this tab genuinely authenticating again.
 */
export const sanctumTokenStorage = {
  read: readToken,
  async get(_app: NuxtApp): Promise<string | undefined> {
    return readToken()
  },
  // Throws SessionTokenError for a malformed token; storage is left untouched.
  async set(_app: NuxtApp, token?: string, remember = false): Promise<void> {
    if (token !== undefined && !isValidSanctumToken(token))
      throw new SessionTokenError('token-malformed')
    if (typeof window === 'undefined') return
    if (token === undefined) {
      if (sessionStorage.getItem(TAB_LOGGED_OUT_KEY) !== null) return
      if (sessionStorage.getItem(SANCTUM_TOKEN_KEY) !== null) {
        sessionStorage.removeItem(SANCTUM_TOKEN_KEY)
      } else if (ownsLocalStorageToken()) {
        clearLocalStorageToken()
      }
      sessionStorage.setItem(TAB_LOGGED_OUT_KEY, 'true')
      return
    }
    sessionStorage.removeItem(TAB_LOGGED_OUT_KEY)
    sessionStorage.removeItem(SANCTUM_TOKEN_KEY)
    if (remember) {
      // Deliberately unconditional, even when a different tab currently owns
      // the remembered slot: "remember me" means "persist this login as the
      // one remembered identity for this browser," and this app supports
      // exactly one such identity at a time by design -- that is what the
      // single shared localStorage key IS, not a bug this file can fix. A
      // user who explicitly opts into being remembered must actually get
      // remembered, not have that choice silently downgraded to a tab-only
      // session because some other tab remembered a different account
      // first. The real, accepted tradeoff: that other tab's own next
      // `get()` (it has no sessionStorage entry of its own, since it was
      // relying on this same localStorage slot) will pick up this new
      // token too, both tabs then use the newly remembered account until
      // one of them logs in or out again. Fully isolating simultaneous
      // remembered sessions per tab would need a real multi-account
      // architecture (e.g. per-tab-scoped remembered slots), which is a
      // deliberate, separate feature decision, not something to bolt on
      // silently here.
      localStorage.setItem(SANCTUM_TOKEN_KEY, token)
      localStorage.setItem(SANCTUM_TOKEN_OWNER_KEY, tabId())
      return
    }
    if (ownsLocalStorageToken()) clearLocalStorageToken()
    sessionStorage.setItem(SANCTUM_TOKEN_KEY, token)
  }
}
